import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { serviceApi, userApi } from '../lib/api';
import {
  Plus, Trash2, Clock, DollarSign, Loader2, Briefcase, Video, Phone,
  Package, FileText, Edit, ExternalLink, Calendar
} from 'lucide-react';

const SERVICE_TYPE_CONFIG = {
  standard: { label: 'Standard Service', icon: Briefcase, description: 'In-person or standard service' },
  video_message: { label: 'Video Message', icon: Video, description: 'Personalized video (like Cameo)' },
  video_call: { label: 'Video Call', icon: Phone, description: 'Live video consultation' },
  rental: { label: 'Rental', icon: Package, description: 'Items or spaces for rent' },
  deliverable: { label: 'Deliverable', icon: FileText, description: 'Digital work delivery' },
};

const RENTAL_SUBCATEGORIES = [
  'Props', 'Backdrops', 'Equipment', 'Vehicles (Display)', 
  'Decor', 'Furniture', 'Spaces/Studios', 'Wardrobe/Costumes', 'Other'
];

const DELIVERABLE_TYPES = ['video', 'audio', 'image', 'document', 'file', 'other'];

const MyServices = () => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const [newService, setNewService] = useState({
    title: '',
    description: '',
    price: '',
    duration_minutes: '60',
    category: '',
    service_type: 'standard',
    custom_category: '',
    meeting_link: '',
    rental_sub_category: '',
    rental_item_description: '',
    deliverable_type: '',
    turnaround_days: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, catsRes] = await Promise.all([
        serviceApi.getMy(),
        userApi.getCategories()
      ]);
      setServices(servicesRes.data || []);
      setCategories(catsRes.data || []);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewService({
      title: '',
      description: '',
      price: '',
      duration_minutes: '60',
      category: '',
      service_type: 'standard',
      custom_category: '',
      meeting_link: '',
      rental_sub_category: '',
      rental_item_description: '',
      deliverable_type: '',
      turnaround_days: ''
    });
    setEditingService(null);
  };

  const handleCreate = async () => {
    if (!newService.title || !newService.description || !newService.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!newService.category) {
      toast.error('Please select a category');
      return;
    }

    setCreateLoading(true);
    try {
      const serviceData = {
        title: newService.title,
        description: newService.description,
        price: parseFloat(newService.price),
        duration_minutes: parseInt(newService.duration_minutes) || 0,
        category: newService.category,
        service_type: newService.service_type,
        custom_category: newService.category === 'Other' ? newService.custom_category : null,
        meeting_link: newService.service_type === 'video_call' ? newService.meeting_link : null,
        rental_sub_category: newService.service_type === 'rental' ? newService.rental_sub_category : null,
        rental_item_description: newService.service_type === 'rental' ? newService.rental_item_description : null,
        deliverable_type: newService.service_type === 'deliverable' ? newService.deliverable_type : null,
        turnaround_days: newService.service_type === 'deliverable' ? parseInt(newService.turnaround_days) || null : null
      };

      if (editingService) {
        await serviceApi.update(editingService.service_id, serviceData);
        toast.success('Service updated!');
      } else {
        await serviceApi.create(serviceData);
        toast.success('Service created!');
      }
      
      setCreateOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setNewService({
      title: service.title || '',
      description: service.description || '',
      price: service.price?.toString() || '',
      duration_minutes: service.duration_minutes?.toString() || '60',
      category: service.category || '',
      service_type: service.service_type || 'standard',
      custom_category: service.custom_category || '',
      meeting_link: service.meeting_link || '',
      rental_sub_category: service.rental_sub_category || '',
      rental_item_description: service.rental_item_description || '',
      deliverable_type: service.deliverable_type || '',
      turnaround_days: service.turnaround_days?.toString() || ''
    });
    setCreateOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await serviceApi.delete(serviceId);
      toast.success('Service deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Check if user has bookee role
  const hasBookeeRole = user?.roles?.includes('bookee') || user?.user_type === 'talent' || user?.user_type === 'both';

  if (!hasBookeeRole) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-primary mb-4" />
            <h2 className="text-xl font-bold mb-2">Become a Bookee First</h2>
            <p className="text-muted-foreground mb-4">
              You need to have a Bookee role to create services.
            </p>
            <Button onClick={() => window.location.href = '/settings'} className="rounded-full">
              Go to Settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ServiceTypeIcon = ({ type }) => {
    const config = SERVICE_TYPE_CONFIG[type] || SERVICE_TYPE_CONFIG.standard;
    const Icon = config.icon;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="my-services-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              My Services
            </h1>
            <p className="text-muted-foreground">
              Manage your service offerings
            </p>
          </div>

          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full btn-glow" data-testid="add-service-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Syne, sans-serif' }}>
                  {editingService ? 'Edit Service' : 'Create New Service'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Service Type Selection */}
                <div className="space-y-2">
                  <Label>Service Type *</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(SERVICE_TYPE_CONFIG).map(([type, config]) => (
                      <Button
                        key={type}
                        type="button"
                        variant={newService.service_type === type ? 'default' : 'outline'}
                        className="h-auto py-3 px-3 flex flex-col items-center gap-1"
                        onClick={() => setNewService({ ...newService, service_type: type })}
                      >
                        <config.icon className="h-5 w-5" />
                        <span className="text-xs">{config.label}</span>
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {SERVICE_TYPE_CONFIG[newService.service_type]?.description}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Service Title *</Label>
                  <Input
                    placeholder="e.g., DJ Set for Events"
                    value={newService.title}
                    onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                    className="rounded-xl"
                    data-testid="service-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={newService.category}
                    onValueChange={(value) => setNewService({ ...newService, category: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="service-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Category if "Other" selected */}
                {newService.category === 'Other' && (
                  <div className="space-y-2">
                    <Label>Custom Category *</Label>
                    <Input
                      placeholder="Enter your custom category"
                      value={newService.custom_category}
                      onChange={(e) => setNewService({ ...newService, custom_category: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                )}

                {/* Rental-specific fields */}
                {newService.service_type === 'rental' && (
                  <>
                    <div className="space-y-2">
                      <Label>Rental Type</Label>
                      <Select
                        value={newService.rental_sub_category}
                        onValueChange={(value) => setNewService({ ...newService, rental_sub_category: value })}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Select rental type" />
                        </SelectTrigger>
                        <SelectContent>
                          {RENTAL_SUBCATEGORIES.map((sub) => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Item Details</Label>
                      <Input
                        placeholder="Describe the rental item"
                        value={newService.rental_item_description}
                        onChange={(e) => setNewService({ ...newService, rental_item_description: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                  </>
                )}

                {/* Video Call specific fields */}
                {newService.service_type === 'video_call' && (
                  <div className="space-y-2">
                    <Label>Meeting Link (Zoom/Google Meet)</Label>
                    <Input
                      placeholder="https://zoom.us/j/..."
                      value={newService.meeting_link}
                      onChange={(e) => setNewService({ ...newService, meeting_link: e.target.value })}
                      className="rounded-xl"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your personal meeting link. Leave blank to add per-booking.
                    </p>
                  </div>
                )}

                {/* Deliverable specific fields */}
                {newService.service_type === 'deliverable' && (
                  <>
                    <div className="space-y-2">
                      <Label>Deliverable Type</Label>
                      <Select
                        value={newService.deliverable_type}
                        onValueChange={(value) => setNewService({ ...newService, deliverable_type: value })}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="What will you deliver?" />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERABLE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Turnaround Time (Days)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 3"
                        value={newService.turnaround_days}
                        onChange={(e) => setNewService({ ...newService, turnaround_days: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    placeholder="Describe your service in detail..."
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    className="rounded-xl"
                    rows={3}
                    data-testid="service-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price ($) *</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newService.price}
                      onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                      className="rounded-xl"
                      data-testid="service-price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (min)</Label>
                    <Input
                      type="number"
                      placeholder="60"
                      value={newService.duration_minutes}
                      onChange={(e) => setNewService({ ...newService, duration_minutes: e.target.value })}
                      className="rounded-xl"
                      data-testid="service-duration-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {newService.service_type === 'rental' ? 'Minimum rental hours' : 
                       newService.service_type === 'deliverable' ? 'N/A for deliverables' : 
                       'Service duration'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => { setCreateOpen(false); resetForm(); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 rounded-xl btn-glow"
                    onClick={handleCreate}
                    disabled={createLoading}
                    data-testid="create-service-submit"
                  >
                    {createLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingService ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingService ? 'Update Service' : 'Create Service'
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : services.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Services Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first service to start accepting bookings
              </p>
              <Button 
                onClick={() => setCreateOpen(true)} 
                className="rounded-full btn-glow"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Service
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {services.map((service) => (
              <Card key={service.service_id} className="border-border/50" data-testid={`service-card-${service.service_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{service.title}</h3>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <ServiceTypeIcon type={service.service_type} />
                          {SERVICE_TYPE_CONFIG[service.service_type || 'standard']?.label}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                        {service.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <Badge>{service.category}</Badge>
                        {service.custom_category && (
                          <Badge variant="secondary">{service.custom_category}</Badge>
                        )}
                        <span className="flex items-center gap-1 text-primary font-medium">
                          <DollarSign className="h-4 w-4" />
                          ${service.price}
                        </span>
                        {service.duration_minutes > 0 && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            {service.duration_minutes} min
                          </span>
                        )}
                        {service.turnaround_days && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {service.turnaround_days} day turnaround
                          </span>
                        )}
                        {service.meeting_link && (
                          <a 
                            href={service.meeting_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-500 hover:underline"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Meeting Link
                          </a>
                        )}
                        {service.rental_sub_category && (
                          <Badge variant="secondary">{service.rental_sub_category}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="rounded-full"
                        onClick={() => handleEdit(service)}
                        data-testid={`edit-service-${service.service_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="icon" 
                        className="rounded-full"
                        onClick={() => handleDelete(service.service_id)}
                        data-testid={`delete-service-${service.service_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServices;
